import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import type {
  DocumentRecord,
  InvoiceFields,
  OcrJobRecord,
  UserRecord,
} from "@docs-organizer/shared";
import { isSuperUser } from "@docs-organizer/shared";
import { api } from "./api";
import { LanguageSwitcher, useI18n } from "./i18n/I18nProvider";
import type { MessageKey } from "./i18n/messages";

type UploadNotice = { id: string; name: string; state: string };

function formatMoney(fields: InvoiceFields, language: string): string {
  if (fields.total == null) return "—";
  const currency = fields.currency ?? "EUR";
  try {
    return new Intl.NumberFormat(language === "pt" ? "pt-PT" : "en-GB", {
      style: "currency",
      currency,
    }).format(fields.total);
  } catch {
    return `${currency} ${fields.total.toFixed(2)}`;
  }
}

export function App(props: {
  user: UserRecord;
  onLogout: () => void;
  onOpenUsers?: () => void;
  onOpenTeams?: () => void;
}) {
  const { t, language } = useI18n();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<OcrJobRecord | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<UploadNotice[]>([]);
  const [draft, setDraft] = useState<InvoiceFields | null>(null);
  const [pending, startTransition] = useTransition();
  const pollRef = useRef<number | null>(null);

  const statusLabels: Record<string, MessageKey> = {
    uploaded: "statusUploaded",
    queued: "statusQueued",
    processing: "statusProcessing",
    completed: "statusCompleted",
    failed: "statusFailed",
  };

  function statusLabel(status: string) {
    const key = statusLabels[status];
    return key ? t(key) : status.replace("_", " ");
  }

  const selected = useMemo(
    () => documents.find((d) => d.id === selectedId) ?? null,
    [documents, selectedId],
  );

  async function refresh(selectId?: string | null) {
    const data = await api.listDocuments({
      q: query || undefined,
      status: statusFilter || undefined,
    });
    setDocuments(data.items);
    const nextId = selectId === undefined ? selectedId : selectId;
    if (nextId) {
      const detail = await api.getDocument(nextId);
      setSelectedJob(detail.job);
      setDraft(detail.document.fields);
      setDocuments((prev) => {
        const others = prev.filter((d) => d.id !== detail.document.id);
        return [detail.document, ...others].sort(
          (a, b) => +new Date(b.createdAt) - +new Date(a.createdAt),
        );
      });
    }
  }

  useEffect(() => {
    startTransition(() => {
      refresh().catch((err) =>
        setError(err instanceof Error ? err.message : t("failedLoad")),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      refresh().catch((err) =>
        setError(err instanceof Error ? err.message : t("failedLoad")),
      );
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, statusFilter]);

  useEffect(() => {
    if (
      !selectedJob ||
      selectedJob.status === "completed" ||
      selectedJob.status === "failed"
    ) {
      if (pollRef.current) window.clearInterval(pollRef.current);
      return;
    }

    pollRef.current = window.setInterval(() => {
      api
        .getJob(selectedJob.id)
        .then((result) => {
          setSelectedJob(result.job);
          if (result.document) {
            setDocuments((prev) =>
              prev.map((d) => (d.id === result.document!.id ? result.document! : d)),
            );
            setDraft(result.document.fields);
          }
          if (result.job.status === "completed" || result.job.status === "failed") {
            refresh(result.job.documentId).catch(() => undefined);
          }
        })
        .catch(() => undefined);
    }, 1500);

    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id, selectedJob?.status]);

  async function processFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of files) {
        const noticeId = `${Date.now()}-${file.name}`;
        setNotices((prev) => [
          ...prev,
          { id: noticeId, name: file.name, state: t("uploading") },
        ]);
        const created = await api.createUpload({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
        });
        await api.uploadFile(created.document.id, created.upload, file);
        setNotices((prev) =>
          prev.map((n) =>
            n.id === noticeId ? { ...n, state: t("queuedOcr") } : n,
          ),
        );
        await api.startOcr(created.document.id);
        setNotices((prev) =>
          prev.map((n) =>
            n.id === noticeId ? { ...n, state: t("processing") } : n,
          ),
        );
        await refresh(created.document.id);
        setSelectedId(created.document.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("uploadFailed"));
    } finally {
      setBusy(false);
      window.setTimeout(() => setNotices([]), 2500);
    }
  }

  async function selectDocument(id: string) {
    setSelectedId(id);
    setBusy(true);
    try {
      const detail = await api.getDocument(id);
      setSelectedJob(detail.job);
      setDraft(detail.document.fields);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("openFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function saveFields() {
    if (!selected || !draft) return;
    setBusy(true);
    try {
      const updated = await api.updateDocument(selected.id, draft);
      setDocuments((prev) =>
        prev.map((d) => (d.id === updated.document.id ? updated.document : d)),
      );
      setDraft(updated.document.fields);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function reprocess() {
    if (!selected) return;
    setBusy(true);
    try {
      const result = await api.startOcr(selected.id);
      setSelectedJob(result.job);
      setDocuments((prev) =>
        prev.map((d) => (d.id === result.document.id ? result.document : d)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ocrFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function reparse() {
    if (!selected) return;
    setBusy(true);
    try {
      const result = await api.reparseDocument(selected.id);
      setDocuments((prev) =>
        prev.map((d) => (d.id === result.document.id ? result.document : d)),
      );
      setDraft(result.document.fields);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("reparseFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function removeSelected() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.deleteDocument(selected.id);
      setSelectedId(null);
      setSelectedJob(null);
      setDraft(null);
      await refresh(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("deleteFailed"));
    } finally {
      setBusy(false);
    }
  }

  const fieldDefs = [
    ["vendor", "fieldVendor"],
    ["invoiceNumber", "fieldInvoiceNumber"],
    ["invoiceDate", "fieldInvoiceDate"],
    ["dueDate", "fieldDueDate"],
    ["currency", "fieldCurrency"],
    ["total", "fieldTotal"],
    ["tax", "fieldTax"],
    ["subtotal", "fieldSubtotal"],
    ["nif", "fieldNif"],
    ["category", "fieldCategory"],
  ] as const;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <h1>{t("brandName")}</h1>
          <p>{t("appTagline")}</p>
        </div>
        <div className="top-actions">
          <LanguageSwitcher className="lang-switcher-compact" />
          <span className="user-chip" title={props.user.email}>
            {props.user.name || props.user.email}
          </span>
          {isSuperUser(props.user) ? (
            <>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={props.onOpenUsers}
              >
                {t("manageUsers")}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={props.onOpenTeams}
              >
                {t("manageTeams")}
              </button>
            </>
          ) : null}
          <button
            className="btn btn-secondary"
            type="button"
            disabled={busy}
            onClick={() =>
              void api.downloadCsv().catch((err) =>
                setError(err instanceof Error ? err.message : t("exportFailed")),
              )
            }
          >
            {t("exportCsv")}
          </button>
          <button
            className="btn"
            type="button"
            disabled={busy}
            onClick={() => refresh().catch(() => undefined)}
          >
            {t("refresh")}
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={props.onLogout}
          >
            {t("signOut")}
          </button>
        </div>
      </header>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="layout">
        <section className="panel">
          <div
            className={`dropzone ${dragging ? "active" : ""}`}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void processFiles(e.dataTransfer.files);
            }}
          >
            <div>
              <h2>{dragging ? t("dropTitleActive") : t("dropTitle")}</h2>
              <p className="hint">{t("dropHint")}</p>
            </div>
            <input
              className="file-input"
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,application/pdf,image/*"
              onChange={(e) => {
                if (e.target.files) void processFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {notices.length > 0 ? (
            <div className="toast-row">
              {notices.map((n) => (
                <span className="chip" key={n.id}>
                  {n.name}: {n.state}
                </span>
              ))}
            </div>
          ) : null}

          <div className="toolbar">
            <input
              placeholder={t("searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t("searchAria")}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label={t("filterStatusAria")}
              style={{ maxWidth: 180 }}
            >
              <option value="">{t("allStatuses")}</option>
              <option value="uploaded">{t("statusUploaded")}</option>
              <option value="queued">{t("statusQueued")}</option>
              <option value="processing">{t("statusProcessing")}</option>
              <option value="completed">{t("statusCompleted")}</option>
              <option value="failed">{t("statusFailed")}</option>
            </select>
          </div>

          {documents.length === 0 ? (
            <div className="empty">
              {pending ? t("loadingDocs") : t("noDocuments")}
            </div>
          ) : (
            <ul className="doc-list">
              {documents.map((doc) => (
                <li
                  key={doc.id}
                  className={`doc-item ${selectedId === doc.id ? "selected" : ""}`}
                  onClick={() => void selectDocument(doc.id)}
                >
                  <div>
                    <strong>{doc.organizedName ?? doc.originalName}</strong>
                    <div className="meta">
                      {doc.fields.vendor ?? t("unknownVendor")} ·{" "}
                      {doc.fields.invoiceDate ?? t("noDate")} ·{" "}
                      {formatMoney(doc.fields, language)}
                    </div>
                  </div>
                  <span className={`status ${doc.status}`}>
                    {statusLabel(doc.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="panel">
          {!selected || !draft ? (
            <div className="empty">{t("selectDocument")}</div>
          ) : (
            <div className="detail">
              <header>
                <div>
                  <h2>{selected.fields.vendor ?? selected.originalName}</h2>
                  <p className="path">
                    {selected.organizedPath ?? selected.originalName}
                  </p>
                </div>
                <span className={`status ${selected.status}`}>
                  {statusLabel(selected.status)}
                </span>
              </header>

              {selectedJob &&
              (selectedJob.status === "pending" ||
                selectedJob.status === "active") ? (
                <div>
                  <div className="meta" style={{ marginBottom: 6 }}>
                    {t("ocrProgress", { progress: selectedJob.progress })}
                  </div>
                  <div className="progress">
                    <span style={{ width: `${selectedJob.progress}%` }} />
                  </div>
                </div>
              ) : null}

              {selected.error ? (
                <div className="error-banner">{selected.error}</div>
              ) : null}

              <div className="fields">
                {fieldDefs.map(([key, labelKey]) => (
                  <div className="field" key={key}>
                    <label htmlFor={key}>{t(labelKey)}</label>
                    <input
                      id={key}
                      value={draft[key] == null ? "" : String(draft[key])}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDraft((prev) => {
                          if (!prev) return prev;
                          if (
                            key === "total" ||
                            key === "tax" ||
                            key === "subtotal"
                          ) {
                            return {
                              ...prev,
                              [key]: value === "" ? null : Number(value),
                            };
                          }
                          return { ...prev, [key]: value || null };
                        });
                      }}
                    />
                  </div>
                ))}
                <div className="field full">
                  <label htmlFor="notes">{t("fieldNotes")}</label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={draft.notes ?? ""}
                    onChange={(e) =>
                      setDraft((prev) =>
                        prev ? { ...prev, notes: e.target.value || null } : prev,
                      )
                    }
                  />
                </div>
              </div>

              {selected.rawText ? (
                <div>
                  <div className="field">
                    <label>{t("rawText")}</label>
                  </div>
                  <div className="raw-text">{selected.rawText}</div>
                </div>
              ) : null}

              <div className="top-actions">
                <button
                  className="btn"
                  type="button"
                  disabled={busy}
                  onClick={() => void saveFields()}
                >
                  {t("saveFields")}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={busy}
                  onClick={() => void reprocess()}
                >
                  {t("rerunOcr")}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={busy || !selected.rawText}
                  onClick={() => void reparse()}
                >
                  {t("reparse")}
                </button>
                <a
                  className="btn btn-secondary"
                  href={api.fileUrl(selected.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("openFile")}
                </a>
                <button
                  className="btn btn-danger"
                  type="button"
                  disabled={busy}
                  onClick={() => void removeSelected()}
                >
                  {t("delete")}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
